import React, { useState, useEffect } from 'react'
import { makeApiCall } from '../utils/customerUtils'

interface ApiKey {
  id: number
  name: string
  last_used_at: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
}

const ApiKeysSettings: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyExpires, setNewKeyExpires] = useState('')
  const [revealedKey, setRevealedKey] = useState<{ id: number; raw: string } | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const fetchKeys = async () => {
    setLoading(true)
    try {
      const res = await makeApiCall('api/api-keys')
      if (res.ok) setKeys(await res.json())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchKeys() }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newKeyName.trim()) return
    setCreating(true)
    try {
      const res = await makeApiCall('api/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName.trim(), expires_at: newKeyExpires || undefined })
      })
      if (res.ok) {
        const data = await res.json()
        setRevealedKey({ id: data.id, raw: data.raw_key })
        setNewKeyName('')
        setNewKeyExpires('')
        setShowCreate(false)
        await fetchKeys()
      }
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (id: number) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return
    const res = await makeApiCall(`api/api-keys/${id}`, { method: 'DELETE' })
    if (res.ok) fetchKeys()
  }

  const fmt = (d: string | null) =>
    d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">API Keys</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          API keys let external systems (like Leadpages or Zapier) submit leads directly
          to your pipeline via <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">POST /api/leads/ingest</code>.
        </p>
      </div>

      {/* Revealed key banner — shown once right after creation */}
      {revealedKey && (
        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 p-4">
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-2">
            Copy your API key now — it will not be shown again.
          </p>
          <div className="flex items-center space-x-2">
            <code className="flex-1 block bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-700 rounded-lg px-3 py-2 text-xs font-mono text-gray-900 dark:text-gray-100 break-all">
              {revealedKey.raw}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedKey.raw); alert('Copied!') }}
              className="px-3 py-2 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex-shrink-0"
            >
              Copy
            </button>
          </div>
          <button
            onClick={() => setRevealedKey(null)}
            className="mt-3 text-xs text-emerald-700 dark:text-emerald-400 hover:underline"
          >
            I've saved it — dismiss
          </button>
        </div>
      )}

      {/* Keys table */}
      {loading ? (
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white" />
        </div>
      ) : keys.length === 0 && !showCreate ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <svg className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">No API keys yet</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Last used</th>
                <th className="px-4 py-3 text-left">Expires</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {keys.map(k => (
                <tr key={k.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{k.name}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{fmt(k.created_at)}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{fmt(k.last_used_at)}</td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{fmt(k.expires_at)}</td>
                  <td className="px-4 py-3">
                    {k.revoked_at ? (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Revoked</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!k.revoked_at && (
                      <button
                        onClick={() => handleRevoke(k.id)}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {showCreate ? (
        <form onSubmit={handleCreate} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">New API Key</h3>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
            <input
              type="text"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              placeholder="e.g. Leadpages webhook"
              required
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Expires (optional)</label>
            <input
              type="date"
              value={newKeyExpires}
              onChange={e => setNewKeyExpires(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 text-sm font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {creating ? 'Creating…' : 'Create Key'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center px-4 py-2 text-sm font-medium bg-gray-900 text-white dark:bg-white dark:text-gray-900 rounded-lg hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New API Key
        </button>
      )}
    </div>
  )
}

export default ApiKeysSettings
